import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate a session ID for the current browser session
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Page name mapping for cleaner analytics reporting
const getPageName = (pathname: string): string => {
  const pageMap: Record<string, string> = {
    '/': 'Home',
    '/v4': 'Identity Pixel',
    '/use-cases': 'Solutions',
    '/competitive-analysis': 'Competitive Analysis',
    '/unlisted': 'Documentation',
    '/newsletter': 'Newsletter',
    '/blog': 'Blog',
    '/auth': 'Authentication',
    '/admin': 'Admin Dashboard',
    '/texas-privacy': 'Texas Privacy',
    '/7-core-segments': '7 Core Segments',
    '/workflow-builder': 'Workflow Builder',
    '/workflow-story': 'Workflow Story',
    '/where-the-data-comes-from': 'Data Source',
    '/how-intent-data-is-gathered': 'Intent Data',
    '/data-source': 'Data Source',
    '/intent': 'Intent Data',
    '/terms-of-service': 'Terms of Service',
    '/contact': 'Contact',
    '/privacy-policy': 'Privacy Policy',
    '/ccpa-privacy': 'CCPA Privacy',
    '/cookie-policy': 'Cookie Policy',
    '/dpa': 'Data Processing Agreement',
    '/opt-out': 'Opt Out',
    '/acceptable-use': 'Acceptable Use Policy',
    '/sla': 'Service Level Agreement',
    '/gdpr-rights': 'GDPR Rights',
    '/disclaimer': 'Disclaimer',
    '/refund-policy': 'Refund Policy',
    '/pricing': 'Pricing',
    '/analytics': 'Analytics Dashboard',
  };

  // Handle dynamic blog post routes
  if (pathname.startsWith('/blog/')) {
    return 'Blog Post';
  }

  return pageMap[pathname] || 'Unknown Page';
};

export const useAnalyticsTracker = () => {
  const location = useLocation();
  const lastTrackedPath = useRef<string>('');

  useEffect(() => {
    // Prevent duplicate tracking for the same path
    if (lastTrackedPath.current === location.pathname) {
      return;
    }
    lastTrackedPath.current = location.pathname;

    // Small delay to ensure document.title is updated
    const timeoutId = setTimeout(async () => {
      const pageName = getPageName(location.pathname);
      const sessionId = getSessionId();

      try {
        await supabase.from('page_views').insert({
          page_path: location.pathname,
          page_name: pageName,
          page_title: document.title,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          session_id: sessionId,
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[Analytics] Failed to track page view:', error);
        }
      }
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
};
